'use client';

import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';
import Link from 'next/link';
import { stegaClean, toPlainText } from 'next-sanity';
import { FC } from 'react';
import { AllFeaturesQueryResult } from '../../sanity_types/theboat_landing_types';
import styles from './AllFeaturesPage.module.scss';

type SingleFeatureInterface = AllFeaturesQueryResult[number];

interface FeaturePage extends SingleFeatureInterface {
  imageUrl?: string;
}

interface SingleFeatureProps {
  data: FeaturePage;
  isActive: boolean;
  handleItemClick: (dataInput: FeaturePage) => void;
}

/** Long enough to be a useful teaser, short enough not to reproduce the detail page. */
const TEASER_LENGTH = 180;

/** Trims to the last whole word within the limit; a mid-word cut reads as a rendering fault. */
const truncateAtWord = (text: string): string => {
  const normalized = text.replace(/\s+/g, ' ').trim();

  if (normalized.length <= TEASER_LENGTH) {
    return normalized;
  }

  const clipped = normalized.slice(0, TEASER_LENGTH);
  const lastSpace = clipped.lastIndexOf(' ');

  return `${(lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).replace(/[\s,;:.–—-]+$/, '')}…`;
};

const SingleFeature: FC<SingleFeatureProps> = ({ data, isActive, handleItemClick }) => {
  const slug = stegaClean(data.slug?.current || '');
  const title = stegaClean(data?.title || '');
  const subtitle = stegaClean(data?.subtitle || '');
  // `subtitle` is a verbatim copy of `title` on roughly half the feature documents ("Logbook" →
  // "Logbook"), so it is only shown beside the title when it says something the title does not.
  const shownSubtitle = subtitle.toLowerCase() === title.toLowerCase() ? '' : subtitle;
  const teaser = truncateAtWord(toPlainText(data?.description ?? []));

  return (
    <div
      className={styles.singleFeaturePageItemContainer}
      onMouseEnter={() => handleItemClick(data)}
      onClick={() => handleItemClick(data)}
      role="button"
      tabIndex={0}
    >
      <Row>
        <Col span={1}>{isActive ? <div className={styles.verticalActiveLine} /> : <div />}</Col>

        <Col span={23}>
          <h2 className={styles.singleFeaturePageItemTitle}>
            {data?.title}
            {shownSubtitle ? <span className={styles.singleFeaturePageItemSubtitle}>– {data?.subtitle}</span> : null}
          </h2>
          {/* A teaser, not the whole description. This rendered each feature's complete body copy —
              the same prose its own /features/[slug] page carries — so the index reproduced all
              twelve detail pages and competed with every one of them for the same terms. That is
              the bulk of the 88 duplicated text blocks the audit found. */}
          <p className={styles.singleFeaturePageItemDescription}>{teaser}</p>
          {/* Absolute, and named. `features/${slug}` only resolved because this list happens to
              render at /features, and all twelve links read "Learn more" — anchor text that
              describes none of them. The feature name is appended out of sight, so the button looks
              exactly as before. */}
          <Link href={`/features/${slug}`}>
            <Button type="link" className={styles.singleFeaturePageItemButton}>
              Learn more
              <span className={styles.srOnly}> about {data?.title}</span>
              {<ArrowRightOutlined />}
            </Button>
          </Link>
        </Col>
      </Row>
    </div>
  );
};

export default SingleFeature;
